import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { Label } from "@/shared/components/ui/label";
import { useCreateDocumentType } from "./use-document-types";
import {
  DOCUMENT_TYPE_DESCRIPTION_MAX,
  DOCUMENT_TYPE_KEY_MAX,
  DOCUMENT_TYPE_NAME_MAX,
} from "../shared/compliance-types";
import { resolveComplianceError } from "../shared/resolve-compliance-error";

const KEY_PATTERN = /^[A-Za-z][A-Za-z0-9_]*$/;

interface Props {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

export function CreateDocumentTypeDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useCreateDocumentType();

  useEffect(() => {
    if (open) {
      setKey("");
      setName("");
      setDescription("");
      setError(null);
    }
  }, [open]);

  const trimmedKey = key.trim();
  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const isValid =
    trimmedKey.length > 0 &&
    trimmedKey.length <= DOCUMENT_TYPE_KEY_MAX &&
    KEY_PATTERN.test(trimmedKey) &&
    trimmedName.length > 0 &&
    trimmedName.length <= DOCUMENT_TYPE_NAME_MAX &&
    trimmedDescription.length <= DOCUMENT_TYPE_DESCRIPTION_MAX;

  const handleSubmit = async () => {
    if (!isValid || create.isPending) return;
    setError(null);
    try {
      await create.mutateAsync({
        key: trimmedKey,
        name: trimmedName,
        description: trimmedDescription.length > 0 ? trimmedDescription : undefined,
      });
      onOpenChange(false);
    } catch (err) {
      setError(resolveComplianceError(err, t));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !create.isPending && onOpenChange(next)}>
      <DialogContent data-testid="compliance-create-document-type-dialog">
        <DialogHeader>
          <DialogTitle>{t("compliance.documentTypes.create.title")}</DialogTitle>
          <DialogDescription>{t("compliance.documentTypes.create.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="dt-key">{t("compliance.documentTypes.fields.key")}</Label>
            <Input
              id="dt-key"
              data-testid="compliance-document-type-key-input"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              maxLength={DOCUMENT_TYPE_KEY_MAX}
              placeholder="MARKETING_CONSENT"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              {t("compliance.documentTypes.fields.keyHint")}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="dt-name">{t("compliance.documentTypes.fields.name")}</Label>
            <Input
              id="dt-name"
              data-testid="compliance-document-type-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={DOCUMENT_TYPE_NAME_MAX}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="dt-description">
              {t("compliance.documentTypes.fields.description")}
            </Label>
            <Textarea
              id="dt-description"
              data-testid="compliance-document-type-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={DOCUMENT_TYPE_DESCRIPTION_MAX}
              rows={3}
            />
          </div>

          {error && (
            <p
              data-testid="compliance-create-document-type-error"
              className="text-sm text-destructive"
            >
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            {t("compliance.actions.cancel")}
          </Button>
          <Button
            data-testid="compliance-create-document-type-submit"
            onClick={handleSubmit}
            disabled={!isValid || create.isPending}
          >
            {create.isPending ? t("compliance.actions.saving") : t("compliance.actions.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
